import { useState } from "react";
import { BLUE, CRITICAL, formatNumber, GOOD, MUTED, MUTED_2, SOFT_BORDER, TEXT } from "./styles";

// How close to the branch average counts as "roughly the same" rather than better/worse.
const AVERAGE_TOLERANCE = 0.05;

function mixHex(hexA, hexB, t) {
  const a = [1, 3, 5].map((i) => parseInt(hexA.slice(i, i + 2), 16));
  const b = [1, 3, 5].map((i) => parseInt(hexB.slice(i, i + 2), 16));
  const [r, g, bl] = a.map((v, i) => Math.round(v + (b[i] - v) * t));
  return `rgb(${r}, ${g}, ${bl})`;
}

// Color at a given raw position (0-100) along the scale, oriented so the "better" end is
// green and the "worse" end is red — left end for lowerIsBetter metrics, right end otherwise.
function goodnessColor(pos, lowerIsBetter) {
  const t = Math.max(0, Math.min(100, pos)) / 100;
  return lowerIsBetter ? mixHex(GOOD, CRITICAL, t) : mixHex(CRITICAL, GOOD, t);
}

// Own-value bar fill: a red↔green gradient from the scale's start up to the value's own
// position, so the color right at the bar's tip shows how good/bad that value is in absolute
// terms — combined with a small blue "Betrieb" marker at the tip so the bar is still identifiable
// as the operator's own value rather than a generic context bar.
function ownBarGradient(ownPos, lowerIsBetter) {
  return `linear-gradient(to right, ${goodnessColor(0, lowerIsBetter)}, ${goodnessColor(ownPos, lowerIsBetter)})`;
}

export function markerPosition(value, max) {
  if (!Number.isFinite(value) || max <= 0) return 0;
  return Math.max(2, Math.min(98, (value / max) * 100));
}

// Position always reflects the raw value (low value = left, high value = right), so for
// lowerIsBetter metrics (e.g. CO2-Intensität) a better value naturally sits further left —
// e.g. Top 25% ends up left of the average.
function ownPosition(value, max, metric) {
  if (!Number.isFinite(metric.scaleMax)) return markerPosition(value, max);
  if (!Number.isFinite(value) || max <= 0) return 0;
  return Math.max(0, Math.min(100, (value / max) * 100));
}

function goodnessPosition(value, max) {
  return markerPosition(value, max);
}

// Returns "better" | "worse" | "average" | null. "average" covers values close enough to the
// branch average (within AVERAGE_TOLERANCE) that calling them better/worse would be misleading.
function compareToAverage(metric, own, average) {
  if (own == null || average == null) return null;
  const spread = Math.abs(average) * AVERAGE_TOLERANCE;
  if (Math.abs(own - average) <= spread) return "average";
  const isBetter = metric.lowerIsBetter ? own < average : own > average;
  return isBetter ? "better" : "worse";
}

// Prefers the backend-provided population bounds (min/max_intensity_of_all) for the bar scale;
// falls back to a heuristic (largest visible value + headroom) when the backend hasn't sent them yet.
function resolveBarMax(metric, value, fallbackValues) {
  if (Number.isFinite(metric.scaleMax) && metric.scaleMax > 0) return metric.scaleMax;

  if (Number.isFinite(value?.max) && value.max > 0) {
    return value.max * 1.05;
  }

  return fallbackValues.length ? Math.max(...fallbackValues) * 1.15 : 1;
}

export function BenchmarkBar({ metric, value, compact }) {
  const own = value?.betrieb;
  const average = value?.branchenschnitt ?? null;
  const top25 = value?.top25 ?? null;
  const hasOwn = own != null;
  const values = [own, average, top25].filter((item) => item != null);
  const max = resolveBarMax(metric, value, values);
  const comparison = compareToAverage(metric, own, average);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: compact ? "0.82rem" : "0.88rem", fontWeight: 700, color: TEXT }}>{metric.label}</div>
          <div style={{ fontSize: "0.74rem", color: MUTED, marginTop: "0.2rem" }}>
            {metric.description || (hasOwn ? "Ihr Betriebswert" : "Betriebswert noch nicht verfügbar")}
          </div>
        </div>
        {comparison != null && (
          <span
            style={{
              borderRadius: 6,
              padding: "0.22rem 0.5rem",
              background: comparison === "better" ? "#ecfdf3" : comparison === "worse" ? "#fef2f2" : "#f1f4f8",
              color: comparison === "better" ? GOOD : comparison === "worse" ? "#b91c1c" : MUTED_2,
              fontSize: "0.72rem",
              fontWeight: 700,
              whiteSpace: "nowrap"
            }}
          >
            {comparison === "better" ? "besser als Durchschnitt" : comparison === "worse" ? "schlechter als Durchschnitt" : "im Durchschnitt"}
          </span>
        )}
      </div>

      <div style={{ marginTop: "0.8rem", display: "flex", alignItems: "baseline", gap: "0.4rem" }}>
        <strong style={{ fontSize: compact ? "1.2rem" : "1.45rem", color: hasOwn ? TEXT : MUTED, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
          {hasOwn ? formatNumber(own, metric.digits) : "-"}
        </strong>
        <span style={{ fontSize: "0.8rem", color: MUTED, fontWeight: 600 }}>{metric.unit}</span>
      </div>

      <div role={hasOwn ? "meter" : undefined} aria-label={hasOwn ? metric.label : undefined} aria-valuenow={hasOwn ? own : undefined} aria-valuemin={hasOwn ? 0 : undefined} aria-valuemax={hasOwn ? max : undefined} style={{ position: "relative", height: 10, background: "#edf1f5", borderRadius: 999, marginTop: "1.05rem", marginBottom: "1.4rem" }}>
        {hasOwn && (
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${ownPosition(own, max, metric)}%`, background: ownBarGradient(ownPosition(own, max, metric), metric.lowerIsBetter), borderRadius: 999 }} />
        )}
        {average != null && <div style={{ position: "absolute", left: `${goodnessPosition(average, max)}%`, top: -5, width: 2, height: 20, background: MUTED_2, borderRadius: 1 }} />}
        {top25 != null && (
          <div
            style={{
              position: "absolute",
              left: `${goodnessPosition(top25, max)}%`,
              top: -5,
              width: 3,
              height: 20,
              background: `repeating-linear-gradient(to bottom, ${GOOD} 0px, ${GOOD} 4px, transparent 4px, transparent 7px)`
            }}
          />
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", fontSize: "0.76rem" }}>
        <div>
          <div style={{ color: MUTED }}>Durchschnitt</div>
          <strong style={{ color: TEXT }}>{average != null ? `${formatNumber(average, metric.digits)} ${metric.unitShort ?? metric.unit}` : "-"}</strong>
        </div>
        <div>
          <div style={{ color: MUTED }}>Top 25%</div>
          <strong style={{ color: TEXT }}>{top25 != null ? `${formatNumber(top25, metric.digits)} ${metric.unitShort ?? metric.unit}` : "-"}</strong>
        </div>
      </div>
    </>
  );
}

function CategoryRow({ category, parentMetric, measure, annotation }) {
  const isHeatingPercentage = ["heating", "booth"].includes(category.key) && category.unit === "%";
  const metric = {
    label: category.label,
    description: category.description,
    unit: category.unit ?? parentMetric.unitShort ?? parentMetric.unit,
    digits: category.digits ?? parentMetric.digits,
    scaleMax: isHeatingPercentage ? 100 : parentMetric.scaleMax,
    lowerIsBetter: category.lowerIsBetter ?? (isHeatingPercentage ? false : parentMetric.lowerIsBetter)
  };
  const own = category?.betrieb;
  const average = category?.branchenschnitt ?? null;
  const top25 = category?.top25 ?? null;
  const hasOwn = own != null;
  const values = [own, average, top25].filter((item) => item != null);
  const max = resolveBarMax(metric, category, values);
  const comparison = compareToAverage(metric, own, average);

  // Show the remaining non-renewable share, with good values on the left.
  const barValue = (value) => isHeatingPercentage ? 100 - value : value;
  const ownBarPosition = ownPosition(barValue(own), max, metric);

  return (
    <div style={{ minHeight: "var(--benchmark-category-height, 0px)", display: "flex", alignItems: "center" }}>
    <div data-benchmark-category-content style={{ width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: "1rem", padding: "0.7rem 0", flexWrap: "wrap" }}>
      <div style={{ flex: "0 0 150px", minWidth: 0 }}>
        <div style={{ fontSize: "0.82rem", fontWeight: 700, color: TEXT }}>{metric.label}</div>
        {metric.description && (
          <div style={{ fontSize: "0.7rem", color: MUTED, marginTop: "0.15rem" }}>{metric.description}</div>
        )}
        {measure && (
          <div style={{ fontSize: "0.66rem", color: BLUE, marginTop: "0.2rem", opacity: 0.8 }}>
            ↳ {measure.name}{measure.targetYear ? ` · ${measure.targetYear}` : ""}
          </div>
        )}
        {annotation && (
          <div style={{ fontSize: "0.66rem", color: MUTED_2, marginTop: "0.2rem", opacity: 0.75 }}>
            {annotation}
          </div>
        )}
      </div>

      <div style={{ flex: "1 1 160px", minWidth: 120 }}>
        <div style={{ position: "relative", height: 8, background: "#edf1f5", borderRadius: 999 }}>
          {hasOwn && (
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${ownBarPosition}%`, background: ownBarGradient(ownBarPosition, isHeatingPercentage || metric.lowerIsBetter), borderRadius: 999 }} />
          )}
          {average != null && <div style={{ position: "absolute", left: `${goodnessPosition(barValue(average), max)}%`, top: -4, width: 2, height: 16, background: MUTED_2, borderRadius: 1 }} />}
          {top25 != null && (
            <div
              style={{
                position: "absolute",
                left: `${goodnessPosition(barValue(top25), max)}%`,
                top: -4,
                width: 3,
                height: 16,
                background: `repeating-linear-gradient(to bottom, ${GOOD} 0px, ${GOOD} 3px, transparent 3px, transparent 6px)`
              }}
            />
          )}
        </div>
      </div>

      <div style={{ flex: "0 0 140px", textAlign: "right", minWidth: 70 }}>
        <strong style={{ fontSize: "0.9rem", color: hasOwn ? TEXT : MUTED, fontVariantNumeric: "tabular-nums" }}>
          {hasOwn ? formatNumber(own, metric.digits) : "-"}
        </strong>{" "}
        <span style={{ fontSize: "0.72rem", color: MUTED }}>{metric.unit}</span>
      </div>

      <div style={{ flex: "0 0 92px", textAlign: "center", minHeight: 24 }}>
        {comparison != null && (
          <span
            style={{
              display: "inline-block",
              borderRadius: 6,
              padding: "0.18rem 0.45rem",
              background: comparison === "better" ? "#ecfdf3" : comparison === "worse" ? "#fef2f2" : "#f1f4f8",
              color: comparison === "better" ? GOOD : comparison === "worse" ? "#b91c1c" : MUTED_2,
              fontSize: "0.68rem",
              fontWeight: 700,
              whiteSpace: "nowrap"
            }}
          >
            {comparison === "better" ? "besser" : comparison === "worse" ? "schlechter" : "durchschnitt"}
          </span>
        )}
      </div>
    </div>
    </div>
  );
}

// Drill-down card: header (or chevron) toggles the sub-category breakdown open/closed,
// collapsed by default on first load (see US-30).
export default function BenchmarkCard({ metric, value, categories, measures, categoryAnnotations, disabled, disabledLabel }) {
  const hasCategories = Array.isArray(categories) && categories.length > 0;
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((prev) => !prev);

  const cardStyle = disabled
    ? {
        position: "relative",
        border: `1px dashed ${SOFT_BORDER}`,
        borderRadius: 10,
        background: "#f8fafb",
        padding: "1rem",
        minWidth: 0,
        opacity: 0.55,
        filter: "grayscale(0.4)",
        userSelect: "none",
        boxShadow: open ? "0 4px 16px rgba(4, 69, 200, 0.12)" : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease"
      }
    : {
        border: `1px solid ${open ? BLUE : SOFT_BORDER}`,
        borderRadius: 10,
        background: "#fff",
        padding: "1rem",
        minWidth: 0,
        boxShadow: open ? "0 4px 16px rgba(4, 69, 200, 0.12)" : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease"
      };

  return (
    <div style={cardStyle}>
      <div style={{ minHeight: "var(--benchmark-summary-height, 0px)" }}>
      <div data-benchmark-summary-content>
      {hasCategories ? (
        <div
          role="button"
          tabIndex={0}
          aria-label={`${metric.label}: Kategorien`}
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && (event.preventDefault(), toggle())}
          style={{ cursor: "pointer" }}
        >
          <div style={{ position: "relative", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.5rem" }}>
            <span
              aria-hidden="true"
              style={{
                fontSize: "0.7rem",
                color: BLUE,
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.15s ease"
              }}
            >
              ▼
            </span>
            {disabled && disabledLabel && (
              <span
                style={{
                  borderRadius: 999,
                  position: "absolute",
                  right: "1.2rem",
                  top: 0,
                  padding: "0.2rem 0.6rem",
                  background: "#e2e8f0",
                  color: MUTED_2,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap"
                }}
              >
                {disabledLabel}
              </span>
            )}
          </div>
          <BenchmarkBar metric={metric} value={value} />
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
          <div style={{ flex: 1 }}>
            <BenchmarkBar metric={metric} value={value} />
          </div>
          {disabled && disabledLabel && (
            <span
              style={{
                borderRadius: 999,
                padding: "0.2rem 0.6rem",
                background: "#e2e8f0",
                color: MUTED_2,
                fontSize: "0.68rem",
                fontWeight: 700,
                whiteSpace: "nowrap"
              }}
            >
              {disabledLabel}
            </span>
          )}
        </div>
      )}
      </div>
      </div>

      {hasCategories && open && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "0.85rem",
            paddingTop: "0.35rem",
            borderTop: `1px dashed ${SOFT_BORDER}`
          }}
        >
          {categories.map((category, index) => (
            <div key={category.key} style={{ borderTop: index === 0 ? "none" : `1px solid ${SOFT_BORDER}` }}>
              <CategoryRow
                category={category}
                parentMetric={metric}
                measure={(measures || []).find((item) => item.category === category.label)}
                annotation={categoryAnnotations?.[category.label]}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
